import React, { useState, useEffect, useRef, useCallback } from "react";
import TaskUserPicker from "./TaskUserPicker";
import {
  REPLY_PREVIEW_LIMIT,
  groupTaskComments,
  hiddenReplyCount,
  visibleReplies,
} from "./taskCommentUtils";

function memberIdsFromTask(task) {
  if (Array.isArray(task?.assignees) && task.assignees.length > 0) {
    return task.assignees.map((person) => person.id);
  }
  return task?.assignee_id ? [task.assignee_id] : [];
}

function watcherIdsFromTask(task) {
  if (!Array.isArray(task?.watchers)) return [];
  return task.watchers.map((person) => person.id);
}

function sameIdList(left, right) {
  if (left.length !== right.length) return false;
  return left.every((id, index) => id === right[index]);
}

const PRIORITIES = [
  { value: "low", label: "Низкий" },
  { value: "medium", label: "Средний" },
  { value: "high", label: "Высокий" },
];

const AUTO_SAVE_LABELS = {
  saving: "Сохранение…",
  saved: "Сохранено",
  error: "Ошибка сохранения",
};

function formatDateTime(value) {
  if (!value) return "";
  return new Date(value).toLocaleString("ru-RU", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDueDate(dueDate) {
  if (!dueDate) return "";
  return new Date(dueDate + "T00:00:00").toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "short",
  });
}

export default function TaskModal({
  isOpen,
  onClose,
  onSave,
  onAutoSave,
  onDelete,
  onAddComment,
  onDeleteComment,
  onUpdateSubtask,
  onCreateSubtask,
  onDeleteSubtask,
  task,
  subtasks = [],
  employees,
  columns,
  boards = [],
  defaultColumnId,
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [assigneeIds, setAssigneeIds] = useState([]);
  const [watcherIds, setWatcherIds] = useState([]);
  const [dueDate, setDueDate] = useState("");
  const [priority, setPriority] = useState("medium");
  const [columnId, setColumnId] = useState("");
  const [boardId, setBoardId] = useState("");
  const [saving, setSaving] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState("idle");
  const [commentText, setCommentText] = useState("");
  const [authorName, setAuthorName] = useState("");
  const [commentSaving, setCommentSaving] = useState(false);
  const [replyToId, setReplyToId] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replyAuthorName, setReplyAuthorName] = useState("");
  const [replySaving, setReplySaving] = useState(false);
  const [expandedReplyRoots, setExpandedReplyRoots] = useState(() => new Set());
  const [newSubtaskTitle, setNewSubtaskTitle] = useState("");
  const [newSubtaskAssigneeId, setNewSubtaskAssigneeId] = useState("");
  const [newSubtaskDueDate, setNewSubtaskDueDate] = useState("");
  const [subtaskSaving, setSubtaskSaving] = useState(false);
  const [editingTitles, setEditingTitles] = useState({});

  const skipAutoSaveRef = useRef(true);
  const savedTimerRef = useRef(null);
  const descriptionRef = useRef("");
  const titleInputRef = useRef(null);
  const taskId = task?.id ?? null;

  const isRootTask = task && !task.parent_task_id;
  const showMembers = !task || !task.parent_task_id;
  const isEditing = Boolean(task);

  const clearSavedTimer = () => {
    if (savedTimerRef.current) {
      clearTimeout(savedTimerRef.current);
      savedTimerRef.current = null;
    }
  };

  const persistChanges = useCallback(
    async (patch) => {
      if (!task || !onAutoSave) return;
      clearSavedTimer();
      setAutoSaveStatus("saving");
      try {
        await onAutoSave(patch);
        setAutoSaveStatus("saved");
        savedTimerRef.current = setTimeout(() => setAutoSaveStatus("idle"), 2000);
      } catch {
        setAutoSaveStatus("error");
      }
    },
    [task, onAutoSave]
  );

  const flushPendingDescription = useCallback(async () => {
    if (!task || !onAutoSave) return;
    const normalized = descriptionRef.current.trim() || null;
    const current = task.description || null;
    if (normalized === current) return;
    await persistChanges({ description: normalized });
  }, [task, onAutoSave, persistChanges]);

  const handleClose = useCallback(async () => {
    if (saving || autoSaveStatus === "saving") return;
    await flushPendingDescription();
    onClose();
  }, [saving, autoSaveStatus, flushPendingDescription, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    skipAutoSaveRef.current = true;
    setAutoSaveStatus("idle");

    if (task) {
      setTitle(task.title || "");
      setDescription(task.description || "");
      descriptionRef.current = task.description || "";
      setAssigneeIds(memberIdsFromTask(task));
      setWatcherIds(watcherIdsFromTask(task));
      setDueDate(task.due_date || "");
      setPriority(task.priority || "medium");
      setColumnId(task.column_id ? String(task.column_id) : "");
      setBoardId(task.board_id ? String(task.board_id) : "");
    } else {
      setTitle("");
      setDescription("");
      descriptionRef.current = "";
      setAssigneeIds([]);
      setWatcherIds([]);
      setDueDate("");
      setPriority("medium");
      setColumnId(defaultColumnId ? String(defaultColumnId) : "");
      setBoardId("");
    }
    setCommentText("");
    setReplyToId(null);
    setReplyText("");
    setReplyAuthorName("");
    setExpandedReplyRoots(new Set());
    setNewSubtaskTitle("");
    setNewSubtaskAssigneeId("");
    setNewSubtaskDueDate("");

    const timer = setTimeout(() => {
      skipAutoSaveRef.current = false;
      titleInputRef.current?.focus();
    }, 0);
    return () => clearTimeout(timer);
    // Reset form only when modal opens or switches to another task id.
    // defaultColumnId: create only — board switch changes columns[0] while editing.
  }, [isOpen, taskId, task ? undefined : defaultColumnId]);

  useEffect(() => {
    if (!isOpen || !task) return;
    setColumnId(task.column_id ? String(task.column_id) : "");
    setBoardId(task.board_id ? String(task.board_id) : "");
  }, [isOpen, taskId, task?.column_id, task?.board_id]);

  useEffect(() => {
    if (!isOpen || !task) return;
    setEditingTitles((prev) => {
      const next = { ...prev };
      for (const s of subtasks) {
        if (next[s.id] === undefined) next[s.id] = s.title || "";
      }
      for (const id of Object.keys(next)) {
        if (!subtasks.some((s) => String(s.id) === String(id))) delete next[id];
      }
      // Keep local edits; only fill missing / remove deleted
      return next;
    });
  }, [isOpen, task, subtasks]);

  useEffect(() => {
    if (!task || skipAutoSaveRef.current) return;
    descriptionRef.current = description;
    const normalized = description.trim() || null;
    const current = task.description || null;
    if (normalized === current) return;

    const timer = setTimeout(() => {
      persistChanges({ description: normalized });
    }, 600);
    return () => clearTimeout(timer);
  }, [description, task, persistChanges]);

  useEffect(() => () => clearSavedTimer(), []);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (saving || autoSaveStatus === "saving") return;
      handleClose();
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [isOpen, saving, autoSaveStatus, handleClose]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isEditing) return;
    if (!title.trim()) return;
    setSaving(true);
    try {
      await onSave({
        title: title.trim(),
        description: description.trim() || null,
        assignee_ids: assigneeIds,
        watcher_ids: watcherIds,
        due_date: dueDate || null,
        priority,
        column_id: columnId ? Number(columnId) : null,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const handleTitleBlur = () => {
    if (!task) return;
    const trimmed = title.trim();
    if (!trimmed || trimmed === task.title) return;
    persistChanges({ title: trimmed });
  };

  const persistMembers = (nextAssignees, nextWatchers) => {
    setAssigneeIds(nextAssignees);
    setWatcherIds(nextWatchers);
    if (!task || skipAutoSaveRef.current) return;
    const currentAssignees = memberIdsFromTask(task);
    const currentWatchers = watcherIdsFromTask(task);
    if (sameIdList(nextAssignees, currentAssignees) && sameIdList(nextWatchers, currentWatchers)) {
      return;
    }
    persistChanges({ assignee_ids: nextAssignees, watcher_ids: nextWatchers });
  };

  const handleAssigneesChange = (ids) => {
    persistMembers(ids, watcherIds.filter((id) => !ids.includes(id)));
  };

  const handleWatchersChange = (ids) => {
    persistMembers(
      assigneeIds.filter((id) => !ids.includes(id)),
      ids
    );
  };

  const handleDueDateChange = (value) => {
    setDueDate(value);
    if (!task || skipAutoSaveRef.current) return;
    const next = value || null;
    if (next === (task.due_date || null)) return;
    persistChanges({ due_date: next });
  };

  const handlePriorityChange = (value) => {
    setPriority(value);
    if (!task || skipAutoSaveRef.current) return;
    if (value === task.priority) return;
    persistChanges({ priority: value });
  };

  const handleColumnChange = (value) => {
    setColumnId(value);
    if (!task || skipAutoSaveRef.current) return;
    const nextId = value ? Number(value) : null;
    if (nextId === task.column_id) return;
    persistChanges({ column_id: nextId });
  };

  const handleBoardChange = (value) => {
    setBoardId(value);
    if (!task || skipAutoSaveRef.current) return;
    const nextId = value ? Number(value) : null;
    if (nextId === task.board_id) return;
    persistChanges({ board_id: nextId });
  };

  const handleDelete = async () => {
    if (!task || !onDelete) return;
    const message =
      task.subtask_count > 0
        ? "Удалить задачу и все подзадачи?"
        : "Удалить задачу?";
    if (!window.confirm(message)) return;
    setSaving(true);
    try {
      await onDelete(task.id);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!task || !onAddComment || !commentText.trim()) return;
    setCommentSaving(true);
    try {
      await onAddComment(task.id, {
        text: commentText.trim(),
        author_name: authorName.trim() || null,
      });
      setCommentText("");
    } finally {
      setCommentSaving(false);
    }
  };

  const handleAddReply = async (event) => {
    event.preventDefault();
    if (!task || !onAddComment || !replyText.trim() || replyToId == null) return;
    const parentId = replyToId;
    setReplySaving(true);
    try {
      await onAddComment(task.id, {
        text: replyText.trim(),
        author_name: replyAuthorName.trim() || null,
        parent_id: parentId,
      });
      setExpandedReplyRoots((prev) => {
        const next = new Set(prev);
        next.add(parentId);
        return next;
      });
      setReplyText("");
      setReplyToId(null);
    } finally {
      setReplySaving(false);
    }
  };

  const toggleRepliesExpanded = (rootId) => {
    setExpandedReplyRoots((prev) => {
      const next = new Set(prev);
      if (next.has(rootId)) next.delete(rootId);
      else next.add(rootId);
      return next;
    });
  };

  const handleDeleteComment = async (commentId) => {
    if (!task || !onDeleteComment) return;
    if (!window.confirm("Удалить комментарий?")) return;
    await onDeleteComment(task.id, commentId);
  };

  const handleToggleSubtask = async (sub) => {
    if (!onUpdateSubtask) return;
    await onUpdateSubtask(sub.id, { is_done: !sub.is_done });
  };

  const handleSubtaskAssignee = async (sub, value) => {
    if (!onUpdateSubtask) return;
    await onUpdateSubtask(sub.id, {
      assignee_id: value ? Number(value) : null,
    });
  };

  const handleSubtaskDueDate = async (sub, value) => {
    if (!onUpdateSubtask) return;
    await onUpdateSubtask(sub.id, { due_date: value || null });
  };

  const handleSubtaskTitleBlur = async (sub) => {
    if (!onUpdateSubtask) return;
    const nextTitle = (editingTitles[sub.id] || "").trim();
    if (!nextTitle || nextTitle === sub.title) return;
    await onUpdateSubtask(sub.id, { title: nextTitle });
  };

  const handleAddSubtask = async (e) => {
    e.preventDefault();
    if (!task || !onCreateSubtask || !newSubtaskTitle.trim()) return;
    setSubtaskSaving(true);
    try {
      await onCreateSubtask(task.id, {
        title: newSubtaskTitle.trim(),
        assignee_id: newSubtaskAssigneeId ? Number(newSubtaskAssigneeId) : null,
        due_date: newSubtaskDueDate || null,
      });
      setNewSubtaskTitle("");
      setNewSubtaskAssigneeId("");
      setNewSubtaskDueDate("");
    } finally {
      setSubtaskSaving(false);
    }
  };

  const handleDeleteSubtask = async (subtaskId) => {
    if (!onDeleteSubtask) return;
    if (!window.confirm("Удалить подзадачу?")) return;
    await onDeleteSubtask(subtaskId);
  };

  const allSubtasksDone =
    task?.subtask_count > 0 && task.subtask_done_count === task.subtask_count;
  const doneColumn = columns.find((c) => c.slug === "done");

  const handleMoveToDone = () => {
    if (!doneColumn) return;
    setColumnId(String(doneColumn.id));
    persistChanges({ column_id: doneColumn.id });
  };

  return (
    <div className="task-modal-overlay" onClick={handleClose}>
      <div
        className="task-modal data-card"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="task-modal-title"
      >
        <div className="task-modal-header">
          <div>
            <h2 id="task-modal-title">{task ? "Редактировать задачу" : "Новая задача"}</h2>
            {task && (
              <p className="task-modal-meta">
                {PRIORITIES.find((p) => p.value === task.priority)?.label || task.priority}
                {task.due_date ? ` · до ${formatDueDate(task.due_date)}` : ""}
              </p>
            )}
          </div>
          <div className="task-modal-header-actions">
            {isEditing && autoSaveStatus !== "idle" && (
              <span
                className={`task-save-status task-save-status-${autoSaveStatus}`}
                aria-live="polite"
              >
                {AUTO_SAVE_LABELS[autoSaveStatus]}
              </span>
            )}
            <button
              type="button"
              className="task-modal-close"
              onClick={handleClose}
              aria-label="Закрыть"
            >
              ×
            </button>
          </div>
        </div>

        <form className="task-modal-form" onSubmit={handleSubmit}>
          <div className="task-modal-body">
          <div className="task-modal-main">
          <div className="task-modal-section">
            <h3 className="task-section-title">Основное</h3>
            <label className="task-form-label">
              Название
              <input
                ref={titleInputRef}
                className="task-form-input"
                name="title"
                autoComplete="off"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onBlur={handleTitleBlur}
                required={!isEditing}
                maxLength={200}
              />
            </label>
            <label className="task-form-label">
              Описание
              <textarea
                className="task-form-textarea"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                maxLength={2000}
              />
            </label>
            {showMembers && (
              <div className="task-form-row task-form-row-members">
                <label className="task-form-label">
                  Исполнители
                  <TaskUserPicker
                    employees={employees}
                    selectedIds={assigneeIds}
                    onChange={handleAssigneesChange}
                    placeholder="Добавить исполнителя"
                  />
                </label>
                <label className="task-form-label">
                  Наблюдатели
                  <TaskUserPicker
                    employees={employees}
                    selectedIds={watcherIds}
                    onChange={handleWatchersChange}
                    placeholder="Добавить наблюдателя"
                  />
                </label>
              </div>
            )}
            <div className="task-form-row">
              <label className="task-form-label">
                Дедлайн
                <input
                  className="task-form-input"
                  type="date"
                  value={dueDate}
                  onChange={(e) => handleDueDateChange(e.target.value)}
                />
              </label>
              <label className="task-form-label">
                Приоритет
                <select
                  className="task-form-select"
                  value={priority}
                  onChange={(e) => handlePriorityChange(e.target.value)}
                >
                  {PRIORITIES.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </label>
              {columns.length > 0 && (
                <label className="task-form-label">
                  Колонка
                  <select
                    className="task-form-select"
                    value={columnId}
                    onChange={(e) => handleColumnChange(e.target.value)}
                  >
                    {columns.map((col) => (
                      <option key={col.id} value={col.id}>
                        {col.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {isRootTask && boards.length > 0 && (
                <label className="task-form-label">
                  Доска
                  <select
                    className="task-form-select"
                    value={boardId}
                    onChange={(e) => handleBoardChange(e.target.value)}
                  >
                    {boards.map((board) => (
                      <option key={board.id} value={board.id}>
                        {board.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>
          </div>

          {isRootTask && (
            <div className="task-modal-section task-subtasks-section">
              <div className="task-subtasks-header">
                <h3 className="task-section-title">
                  Подзадачи
                  {task.subtask_count > 0 && (
                    <span className="task-subtasks-progress">
                      {task.subtask_done_count}/{task.subtask_count}
                    </span>
                  )}
                </h3>
              </div>

              {allSubtasksDone && doneColumn && task.column_id !== doneColumn.id && (
                <div className="task-subtasks-hint-row">
                  <p className="task-subtasks-hint">
                    Все пункты выполнены — можно перенести в «{doneColumn.name}».
                  </p>
                  <button type="button" className="btn btn-sm btn-primary" onClick={handleMoveToDone}>
                    В «{doneColumn.name}»
                  </button>
                </div>
              )}

              {subtasks.length > 0 ? (
                <ul className="task-checklist">
                  {subtasks.map((sub) => (
                    <li
                      key={sub.id}
                      className={`task-checklist-item ${sub.is_done ? "task-checklist-item-done" : ""}`}
                    >
                      <input
                        type="checkbox"
                        className="task-checklist-checkbox"
                        checked={Boolean(sub.is_done)}
                        onChange={() => handleToggleSubtask(sub)}
                      />
                      <input
                        className="task-checklist-title"
                        value={editingTitles[sub.id] ?? sub.title}
                        onChange={(e) =>
                          setEditingTitles((prev) => ({
                            ...prev,
                            [sub.id]: e.target.value,
                          }))
                        }
                        onBlur={() => handleSubtaskTitleBlur(sub)}
                        maxLength={200}
                      />
                      <select
                        className="task-checklist-select"
                        value={sub.assignee_id ? String(sub.assignee_id) : ""}
                        onChange={(e) => handleSubtaskAssignee(sub, e.target.value)}
                        title="Исполнитель"
                      >
                        <option value="">—</option>
                        {employees.map((emp) => (
                          <option key={emp.id} value={emp.id}>
                            {emp.full_name}
                          </option>
                        ))}
                      </select>
                      <input
                        type="date"
                        className="task-checklist-date"
                        value={sub.due_date || ""}
                        onChange={(e) => handleSubtaskDueDate(sub, e.target.value)}
                        title="Дедлайн"
                      />
                      <button
                        type="button"
                        className="task-checklist-delete"
                        onClick={() => handleDeleteSubtask(sub.id)}
                        title="Удалить"
                        aria-label="Удалить подзадачу"
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="task-subtasks-empty">Подзадач пока нет</p>
              )}

              <div className="task-checklist-add">
                <input
                  className="task-form-input"
                  placeholder="Новый пункт..."
                  value={newSubtaskTitle}
                  onChange={(e) => setNewSubtaskTitle(e.target.value)}
                  maxLength={200}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddSubtask(e);
                    }
                  }}
                />
                <select
                  className="task-checklist-select"
                  value={newSubtaskAssigneeId}
                  onChange={(e) => setNewSubtaskAssigneeId(e.target.value)}
                  title="Исполнитель"
                >
                  <option value="">Исполнитель</option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.full_name}
                    </option>
                  ))}
                </select>
                <input
                  type="date"
                  className="task-checklist-date"
                  value={newSubtaskDueDate}
                  onChange={(e) => setNewSubtaskDueDate(e.target.value)}
                  title="Дедлайн"
                />
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={handleAddSubtask}
                  disabled={subtaskSaving || !newSubtaskTitle.trim()}
                >
                  {subtaskSaving ? "..." : "Добавить"}
                </button>
              </div>
            </div>
          )}
          </div>

          {task && onAddComment && (
            <div className="task-modal-side">
            <div className="task-modal-section task-comments-section">
              <h3 className="task-section-title">Комментарии</h3>
              {(task.comments || []).length > 0 ? (
                <div className="task-comments-list">
                  {groupTaskComments(task.comments).map(({ root, replies }) => {
                    const expanded = expandedReplyRoots.has(root.id);
                    const shown = visibleReplies(replies, expanded);
                    const hidden = hiddenReplyCount(replies, expanded);
                    return (
                      <div key={root.id} className="task-comment-thread">
                        <div className="task-comment-item">
                          <div className="task-comment-meta">
                            <span>{root.author_name || "Руководитель"}</span>
                            <span>{formatDateTime(root.created_at)}</span>
                            {onDeleteComment && (
                              <button
                                type="button"
                                className="task-comment-delete"
                                onClick={() => handleDeleteComment(root.id)}
                                aria-label="Удалить комментарий"
                              >
                                ×
                              </button>
                            )}
                          </div>
                          <div className="task-comment-text">{root.text}</div>
                          {onAddComment && (
                            <button
                              type="button"
                              className="task-comment-reply-toggle"
                              onClick={() => {
                                setReplyToId((current) => (current === root.id ? null : root.id));
                                setReplyText("");
                              }}
                              aria-label="Ответить"
                            >
                              Ответить
                            </button>
                          )}
                        </div>
                        {shown.length > 0 && (
                          <div className="task-comment-replies">
                            {shown.map((reply) => (
                              <div key={reply.id} className="task-comment-item task-comment-reply">
                                <div className="task-comment-meta">
                                  <span>{reply.author_name || "Руководитель"}</span>
                                  <span>{formatDateTime(reply.created_at)}</span>
                                  {onDeleteComment && (
                                    <button
                                      type="button"
                                      className="task-comment-delete"
                                      onClick={() => handleDeleteComment(reply.id)}
                                      aria-label="Удалить комментарий"
                                    >
                                      ×
                                    </button>
                                  )}
                                </div>
                                <div className="task-comment-text">{reply.text}</div>
                              </div>
                            ))}
                            {hidden > 0 && (
                              <button
                                type="button"
                                className="task-comment-more"
                                onClick={() => toggleRepliesExpanded(root.id)}
                              >
                                Показать ещё {hidden}
                              </button>
                            )}
                            {expanded && replies.length > REPLY_PREVIEW_LIMIT && (
                              <button
                                type="button"
                                className="task-comment-more"
                                onClick={() => toggleRepliesExpanded(root.id)}
                              >
                                Скрыть
                              </button>
                            )}
                          </div>
                        )}
                        {replyToId === root.id && (
                          <div className="task-comment-form task-comment-reply-form">
                            <input
                              className="task-form-input"
                              placeholder="Ваше имя (необязательно)"
                              value={replyAuthorName}
                              onChange={(e) => setReplyAuthorName(e.target.value)}
                              maxLength={200}
                            />
                            <textarea
                              className="task-form-textarea"
                              placeholder="Ответ"
                              value={replyText}
                              onChange={(e) => setReplyText(e.target.value)}
                              rows={2}
                              maxLength={2000}
                            />
                            <div className="task-comment-reply-actions">
                              <button
                                type="button"
                                className="btn btn-sm"
                                onClick={handleAddReply}
                                disabled={replySaving || !replyText.trim()}
                                aria-label="Отправить ответ"
                              >
                                {replySaving ? "..." : "Ответить"}
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm"
                                onClick={() => setReplyToId(null)}
                              >
                                Отмена
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="task-comments-empty">Комментариев пока нет</p>
              )}
              <div className="task-comment-form">
                <input
                  className="task-form-input"
                  placeholder="Ваше имя (необязательно)"
                  value={authorName}
                  onChange={(e) => setAuthorName(e.target.value)}
                  maxLength={200}
                />
                <textarea
                  className="task-form-textarea"
                  placeholder="Новый комментарий"
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  rows={2}
                  maxLength={2000}
                />
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={handleAddComment}
                  disabled={commentSaving || !commentText.trim()}
                >
                  {commentSaving ? "..." : "Добавить комментарий"}
                </button>
              </div>
            </div>
            </div>
          )}
          </div>

          <div className="task-modal-actions">
            {task && onDelete && (
              <button
                type="button"
                className="btn task-btn-danger"
                onClick={handleDelete}
                disabled={saving}
              >
                Удалить
              </button>
            )}
            <div className="task-modal-actions-right">
              <button type="button" className="btn" onClick={handleClose} disabled={saving}>
                {isEditing ? "Закрыть" : "Отмена"}
              </button>
              {!isEditing && (
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? "Создание..." : "Создать"}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
